<template>
	<div
		class="amelipro-card--multi-folding w-100 mb-3"
		:style="generalCardStyles"
	>
		<div class="amelipro-card--multi-folding__header flex-column align-start">
			<div
				class="w-100 pb-2"
				:style="cardHeaderStyles"
			>
				<div class="w-100 d-flex flex-column flex-sm-row align-center">
					<div
						class="amelipro-card--multi-folding__header--left"
						style="display: inline-block"
						:style="headerLeftStyles"
					>
						<div class="d-flex">
							<AmeliproIcon
								class="d-block mr-2"
								:icon="openedItemId !== null ? 'triangleUp' : 'triangleDown'"
								icon-bg-color="transparent"
								icon-color="ap-blue darken-1"
								size="11px"
								style="margin-top: -7px;"
							/>

							<p
								:aria-level="titleLevel"
								class="font-weight-semibold text-h3 mb-0"
								:class="titleUppercase ? 'text-uppercase' : undefined"
								role="heading"
							>
								{{ title }}
							</p>
						</div>
					</div>

					<div
						v-if="hasRightSlot"
						class="amelipro-card--multi-folding__header--right"
						:style="headerRightStyles"
					>
						<slot name="headerRight" />
					</div>
				</div>

				<ul class="mt-1 list-style-none d-flex flex-wrap align-center">
					<li
						v-for="(item, index) in internalTabs"
						:key="index"
						class="mb-2 mr-2"
					>
						<button
							:id="item.id + '-title'"
							:aria-controls="item.id"
							:aria-expanded="openedItemId === item.id ? 'true' : 'false'"
							class="d-inline-flex align-center amelipro-card--multi-folding__btn"
							:class="btnClasses(item.id, index)"
							type="button"
							@click="openClose(item.id, index)"
							@mouseenter="hover = true"
							@mouseleave="hover = false"
						>
							<AmeliproIcon
								v-if="openedItemId !== item.id && item.valid && item.error !== true"
								class="mr-2 btn-icon"
								icon="check"
								icon-bg-color="ap-turquoise darken-1"
								icon-color="ap-white"
								medium
							/>

							<AmeliproIcon
								v-if="openedItemId !== item.id && item.error"
								class="mr-2 btn-icon"
								icon="sensInterdit"
								icon-bg-color="ap-red"
								icon-color="ap-white"
								medium
							/>

							<AmeliproIcon
								v-if="openedItemId === item.id"
								class="mr-2 btn-icon"
								icon="modifier"
								icon-bg-color="ap-blue darken-1"
								icon-color="ap-white"
								medium
							/>

							{{ item.title }}

							<span
								v-if="openedItemId === item.id"
								class="d-sr-only"
							>
								Actif
							</span>

							<span
								v-if="item.error === true"
								class="d-sr-only"
							>
								Contient des erreurs
							</span>
						</button>
					</li>
				</ul>
			</div>
		</div>

		<div
			v-for="(item, index) in tabs"
			:key="index"
		>
			<div
				v-show="openedItemId === item.id"
				:id="item.id"
				:aria-labelledby="item.id + '-title'"
				class="amelipro-card--multi-folding__content ap-grey--text text--darken-1"
				role="region"
			>
				<slot :name="item.id" />
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { PropType, computed, ref, useSlots, watch } from 'vue';
	import AmeliproIcon from '../AmeliproIcon';
	import { AmeliproMultipleFoldingCardItem } from './types';
	import BreakpointHelper from '../../helpers/BreakpointHelper';
	import { IndexedObject } from '../../types';
	import { convertToHex } from '../../functions/convertToHex';

	const props = defineProps({
		borderColor: {
			type: String,
			default: 'ap-grey lighten-2',
		},
		bordered: {
			type: Boolean,
			default: true,
		},
		cardColor: {
			type: String,
			default: 'ap-white',
		},
		defaultItemOpened: {
			type: [Number, null] as PropType<number | null>,
			default: null,
		},
		headerRightWidth: {
			type: String,
			default: '50%',
		},
		manualValidation: {
			type: Boolean,
			default: false,
		},
		tabs: {
			type: Array as PropType<AmeliproMultipleFoldingCardItem[]>,
			required: true,
		},
		title: {
			type: String,
			required: true,
		},
		titleLevel: {
			type: Number,
			default: 2,
		},
		titleUppercase: {
			type: Boolean,
			default: false,
		},
	});

	const breakpointHelper = new BreakpointHelper();

	// TODO: hover change de valeur mais n'est jamais utilisé. À retirer ou émettre quelque chose ?
	const hover = ref<boolean>(false);
	const openedItemId = ref<string | null>(null);
	const emit = defineEmits(['tab-clicked']);

	const internalTabs = ref<AmeliproMultipleFoldingCardItem[]>([]);
	const openDefaultItem = (): void => {
		if (props.defaultItemOpened !== null) {
			openedItemId.value = internalTabs.value[props.defaultItemOpened].id;
			if (props.manualValidation === false) {
				internalTabs.value[props.defaultItemOpened].valid = true;
			}
		}
	};
	watch(() => props.tabs, (newValue: AmeliproMultipleFoldingCardItem[]) => {
		internalTabs.value = newValue;
		openDefaultItem();
	}, { immediate: true });

	const openClose = (id: string, index: number): void => {
		if (props.manualValidation === false) {
			internalTabs.value[index].valid = true;
		}
		openedItemId.value = openedItemId.value === id ? null : id;
		emit('tab-clicked', id);
	};

	const btnClasses = (id: string, index: number): string => {
		if (openedItemId.value === id) {
			return 'classic-btn active-btn';
		} else if (internalTabs.value[index].error === true) {
			return 'classic-btn error-btn';
		} else if (internalTabs.value[index].valid === true) {
			return 'classic-btn checked-btn';
		}

		return 'classic-btn';
	};

	const generalCardStyles = computed<IndexedObject>(() => {
		const cardStyle: IndexedObject = { backgroundColor: `${convertToHex(props.cardColor)}` };

		if (props.bordered) {
			cardStyle.border = `1px solid ${convertToHex(props.borderColor)}`;
		}

		return cardStyle;
	});

	const cardHeaderStyles = computed<IndexedObject | undefined>(() => {
		if (openedItemId.value !== null) {
			return { borderBottom: `1px solid ${convertToHex(props.borderColor)}` };
		}
		return undefined;
	});

	const slots = useSlots();
	const hasRightSlot = computed<boolean>(() => Boolean(slots.headerRight));

	const headerLeftStyles = computed<IndexedObject>((): IndexedObject => {
		if (hasRightSlot.value && breakpointHelper.is('smAndUp')) {
			return {
				width: `calc(100% - (${props.headerRightWidth} + 10px))`,
				marginRight: '10px',
			};
		}
		return { width: '100%' };
	});

	const headerRightStyles = computed<IndexedObject>((): IndexedObject => {
		if (hasRightSlot.value && breakpointHelper.is('smAndUp')) {
			return {
				width: `calc(${props.headerRightWidth} - 10px)`,
				marginLeft: '10px',
			};
		}
		return { width: '100%' };
	});

	// expose openClose besause it is used by developers on services
	defineExpose({ openClose });
</script>

<style lang="scss" scoped>
.amelipro-card--multi-folding {
	display: block;
	position: relative;
	max-width: 100%;
	border-radius: $card-radius;
	text-decoration: none;
	overflow-wrap: break-word;
	white-space: normal;
}

.amelipro-card--multi-folding__header {
	padding: $card-padding--desktop;
	word-break: break-all;

	@media #{$media-down-md} {
		padding: $card-padding--tablet;
	}

	@media #{$media-down-sm} {
		padding: $card-padding--mobile;
	}

	padding-bottom: 0 !important;
}

.amelipro-card--multi-folding__content {
	width: 100%;
	padding: $card-padding--desktop;
	font-size: $font-size-xs;
	font-weight: $ap-font-weight-regular;

	@media #{$media-down-md} {
		padding: $card-padding--tablet;
	}

	@media #{$media-down-sm} {
		padding: $card-padding--mobile;
	}

	padding-top: $card-padding--desktop;
}

.classic-btn {
	position: relative;
	padding: $filter-padding-y $filter-padding-x;
	border: 1px solid $ap-blue-darken1;
	border-radius: $filter-radius;
	background-color: $ap-white;
	color: $ap-blue-darken1;
	font-size: $font-size-sm;
	font-weight: $filter-font-weight;
	cursor: pointer;

	&.active-btn {
		background-color: $ap-blue-lighten3;
	}

	&.checked-btn {
		border: 1px solid $ap-turquoise-darken1;
		background-color: $ap-turquoise-lighten1;
		color: $ap-turquoise-darken1;
	}

	&.error-btn {
		border: 1px solid $ap-red-darken1;
		background-color: $ap-red-lighten3;
		color: $ap-red-darken1;
	}

	&:focus {
		outline: 1px dotted $ap-grey-darken1;
	}

	&:disabled {
		cursor: default;
	}
}

.btn-icon {
	position: absolute;
	top: calc(50% - 13px);
	left: 0.8rem;
}
</style>
